<template>
  <v-container class="fill-height">
    <v-row align="center" justify="center">
      <v-col cols="12" sm="7" md="5" lg="4">
        <v-card>
          <template v-if="!error">
            <v-card-title v-if="route.meta.new_user">
              Confirmar email
            </v-card-title>
            <v-card-title v-else> Redefinir senha </v-card-title>
            <v-card-text class="mb-10 pt-0">
              <v-expand-transition>
                <v-row v-if="form.password.length >= 1">
                  <v-col cols="12">
                    <password-rule-status
                      v-for="(rule, i) in rules"
                      :key="i"
                      :valid="rule.valid"
                    >
                      {{ rule.description }}
                    </password-rule-status>
                  </v-col>
                </v-row>
              </v-expand-transition>
              <v-form ref="formRef" @submit.prevent="onSubmit">
                <v-text-field
                  :prepend-inner-icon="mdiLock"
                  type="password"
                  v-model="form.password"
                  :rules="[validationRules.required]"
                  label="Nova senha"
                  autocomplete="new-password"
                />

                <v-text-field
                  :prepend-inner-icon="mdiLockOutline"
                  v-model="form.password2"
                  :rules="[
                    validationRules.required,
                    validationRules.confirmed(form.password),
                  ]"
                  label="Confirme a senha"
                  type="password"
                  autocomplete="new-password"
                />

                <v-btn
                  class="text-capitalize mt-3"
                  block
                  color="primary"
                  type="submit"
                  :loading="submitting"
                >
                  <template v-if="submitting">
                    <span>Redefinido...</span>
                  </template>
                  <template v-else>
                    <span>Redefinir senha</span>
                  </template>
                </v-btn>
              </v-form>
            </v-card-text>
          </template>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { mdiLock, mdiLockOutline } from "@mdi/js";
import { useRoute, useRouter } from "vue-router";
import { validationRules } from "@/utils/validation";
import { useResetPasswordMutation } from "@/queries/modules/users";
import PasswordRuleStatus from "@/views/grants/PasswordRuleStatus.vue";
import { useSnackbar } from "@/plugins/snackbar";

const route = useRoute();
const router = useRouter();
const formRef = ref(null);
const $snackbar = useSnackbar();

const resetPasswordMutation = useResetPasswordMutation();

const error = ref(false);
const submitting = computed(() => resetPasswordMutation.isPending.value);
const token = ref("");

const form = reactive({
  password: "",
  password2: "",
});

const hasLowerCase = computed(() => {
  return form.password.toUpperCase() !== form.password;
});

const hasUpperCase = computed(() => {
  return form.password.toLowerCase() !== form.password;
});

const hasNumber = computed(() => {
  return /\d/.test(form.password);
});

const hasSpecialCaracter = computed(() => {
  return /[!@#$%^&*()\-_=+{};:,<.>]/.test(form.password);
});

const hasMinLength = computed(() => {
  return form.password.length >= 8;
});

const allRulesValid = computed(() => {
  return (
    hasLowerCase.value &&
    hasUpperCase.value &&
    hasNumber.value &&
    hasMinLength.value &&
    hasSpecialCaracter.value
  );
});

const rules = computed(() => {
  return [
    {
      description: "Pelo menos uma letra minúscula",
      valid: hasLowerCase.value,
    },
    {
      description: "Pelo menos uma letra maiúscula",
      valid: hasUpperCase.value,
    },
    {
      description: "Pelo menos um número",
      valid: hasNumber.value,
    },
    {
      description: "Pelo menos um caractere especial",
      valid: hasSpecialCaracter.value,
    },
    {
      description: "Pelo menos 8 caracteres",
      valid: hasMinLength.value,
    },
  ];
});

onMounted(() => {
  token.value = route.query.token;
});

async function onSubmit() {
  const { valid } = await formRef.value.validate();
  if (!valid) {
    return;
  }

  if (valid && allRulesValid.value) {
    try {
      await resetPasswordMutation.mutateAsync({
        token: token.value,
        password: form.password,
      });

      if (route.meta.new_user) {
        $snackbar.info("Email confimado");
      } else {
        $snackbar.info("Senha redefinida");
      }
      router.replace("/login");
    } catch (e) {
      $snackbar.error(e.message);
    }
  }
}
</script>

<style scoped></style>
